import React, { useEffect, useState } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Users2, ShieldCheck, UserCog, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function SuperAdmin() {
  const { impersonate, user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [o, n] = await Promise.all([
          api.get("/super-admin/orgs"),
          api.get("/super-admin/notifications-log", { params: { limit: 30 } }),
        ]);
        setRows(o.data);
        setNotifs(n.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const go = async (orgId) => {
    try {
      await impersonate(orgId);
      toast.success("Impersonating — view logged in audit");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (user?.role !== "super_admin") {
    return (
      <SuperAdminLayout>
        <div className="eb-card p-8 text-center" data-testid="super-admin-denied">
          <ShieldCheck className="w-10 h-10 mx-auto text-navy-300 mb-3" />
          <h1 className="font-semibold">Super admin only</h1>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-5 h-5 text-orange-500" />
        <div className="eb-overline">Super admin</div>
      </div>
      <h1 className="text-3xl font-semibold mb-1" data-testid="super-admin-heading">Organisations</h1>
      <p className="text-sm text-navy-500 mb-8">Manage every tenant. Impersonation is fully audit-logged with start/end markers.</p>

      <div className="eb-card overflow-hidden">
        {loading ? (
          <p className="p-6 text-navy-500">Loading…</p>
        ) : (
          <table className="w-full text-sm" data-testid="orgs-table">
            <thead className="bg-surface text-xs uppercase tracking-wider text-navy-500">
              <tr>
                <th className="text-left px-5 py-3">Org</th>
                <th className="text-left px-5 py-3">Country</th>
                <th className="text-left px-5 py-3">Users</th>
                <th className="text-left px-5 py-3">Sites</th>
                <th className="text-left px-5 py-3">Plan</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-navy-100 hover:bg-surface/50" data-testid={`org-row-${o.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-navy-600" />
                      <span className="font-medium">{o.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-navy-600">{o.country}</td>
                  <td className="px-5 py-3 flex items-center gap-1 text-navy-600"><Users2 className="w-3 h-3" /> {o.user_count}</td>
                  <td className="px-5 py-3 text-navy-600">{o.site_count}</td>
                  <td className="px-5 py-3 capitalize text-navy-700 font-medium">{o.plan || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                      o.status === "active" ? "bg-success/15 text-success" :
                      o.status === "trialing" ? "bg-orange-100 text-orange-700" :
                      "bg-navy-100 text-navy-600"
                    }`}>{o.status || "?"}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => go(o.id)} className="btn-outline text-xs" data-testid={`impersonate-${o.id}`}>
                      <UserCog className="w-3.5 h-3.5" /> Impersonate
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-10 text-center text-navy-500">No organisations yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-semibold mb-3">Recent mock notifications</h2>
        <div className="eb-card overflow-hidden">
          {notifs.length === 0 ? (
            <p className="p-5 text-navy-500 text-sm">No mock notifications yet.</p>
          ) : (
            <div className="divide-y divide-navy-100">
              {notifs.map((n) => (
                <div key={n.id} className="px-5 py-3 text-sm flex flex-wrap items-center gap-3" data-testid={`super-notif-${n.id}`}>
                  <span className="eb-mocked-badge">{n.service}</span>
                  <span className="font-medium">{n.subject || n.type}</span>
                  <span className="text-navy-500 text-xs">{n.recipient}</span>
                  <span className="ml-auto text-navy-400 text-xs">{new Date(n.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
